/**
 * Hero right column — an idea assembling into a live, connected, intelligent product.
 * Phases: idea → build → intelligence → connected → production (driven by useHeroAnimation).
 */
import React, {useEffect, useState} from 'react';
import {animate, AnimatePresence, motion, useMotionValue, useSpring, useTransform} from "framer-motion";
import {heroProductPhases, heroConnectionNodes} from "../data";
import {HERO_PHASES, phaseDuration} from "./useHeroAnimation";

const EASE = [0.22, 1, 0.36, 1];

/** Orbit node positions (% of the stage). Order matches heroConnectionNodes. */
const NODE_POS = [
    {x: 12, y: 12},
    {x: 88, y: 12},
    {x: 95, y: 50},
    {x: 90, y: 92},
    {x: 10, y: 92},
    {x: 5, y: 50}
];

/** Phones: the app fills the width, so nodes sit in bands above and below it. */
const NODE_POS_NARROW = [
    {x: 16, y: 5},
    {x: 50, y: 2},
    {x: 84, y: 5},
    {x: 84, y: 95},
    {x: 50, y: 98},
    {x: 16, y: 95}
];

function useIsNarrow(query = "(max-width: 768px)") {
    const [narrow, setNarrow] = useState(() =>
        typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false);
    useEffect(() => {
        const mq = window.matchMedia(query);
        const apply = () => setNarrow(mq.matches);
        apply();
        mq.addEventListener("change", apply);
        return () => mq.removeEventListener("change", apply);
    }, [query]);
    return narrow;
}

const FRAGMENTS = [
    {id: "btn", x: 18, y: 24, kind: "btn"},
    {id: "toggle", x: 80, y: 22, kind: "toggle"},
    {id: "chart", x: 20, y: 76, kind: "chart"},
    {id: "avatar", x: 80, y: 74, kind: "avatar"}
];

const METRICS = [
    {label: "Users", value: 1284, format: (v) => v.toLocaleString("en-US")},
    {label: "Uptime", value: 999, format: (v) => `${(v / 10).toFixed(1)}%`},
    {label: "Latency", value: 42, format: (v) => `${v}ms`}
];

const TASKS = ["Sync new leads", "Score intent", "Draft follow-up"];

const EVENTS = ["New user signed up", "Payment processed", "Report generated", "Lead synced to CRM"];

const IDEA_TEXT = "An idea…";

function AnimatedNumber({value, format, run}) {
    const [v, setV] = useState(run ? value : 0);
    useEffect(() => {
        if (!run) {
            setV(0);
            return undefined;
        }
        const controls = animate(0, value, {duration: 1.4, ease: EASE, onUpdate: (n) => setV(Math.round(n))});
        return () => controls.stop();
    }, [run, value]);
    return <>{format(v)}</>;
}

function TypedIdea({run}) {
    const [n, setN] = useState(run ? 0 : IDEA_TEXT.length);
    useEffect(() => {
        if (!run) return undefined;
        setN(0);
        let i = 0;
        const id = setInterval(() => {
            i += 1;
            setN(i);
            if (i >= IDEA_TEXT.length) clearInterval(id);
        }, 85);
        return () => clearInterval(id);
    }, [run]);
    return (
        <span className="hx-idea-text">
            {IDEA_TEXT.slice(0, n)}<span className="hx-caret"/>
        </span>
    );
}

function Fragment({f, i, gathered}) {
    return (
        <motion.span
            className={`hx-frag is-${f.kind}`}
            initial={false}
            animate={gathered
                ? {left: "50%", top: "50%", opacity: 0, scale: 0.35}
                : {left: `${f.x}%`, top: `${f.y}%`, opacity: 1, scale: 1}}
            transition={{duration: gathered ? 0.7 : 0.8, ease: EASE, delay: gathered ? i * 0.04 : 0.15 + i * 0.08}}
        >
            {f.kind === "btn" && <span className="hx-frag-btn"/>}
            {f.kind === "toggle" && <span className="hx-frag-toggle"><i/></span>}
            {f.kind === "chart" && <span className="hx-frag-bars"><i/><i/><i/></span>}
            {f.kind === "avatar" && <span className="hx-frag-avatar"/>}
        </motion.span>
    );
}

function Stepper({idx, phase, hoverSlow, reducedMotion}) {
    const ms = phaseDuration(phase, hoverSlow);
    return (
        <ol className="hx-steps" aria-hidden="true">
            {heroProductPhases.map((p, i) => (
                <li key={p.id} className={i === idx ? "is-active" : i < idx ? "is-done" : ""}>
                    <span className="hx-step-label">{p.label}</span>
                    <span className="hx-step-bar">
                        {i < idx && <span className="hx-step-fill is-full"/>}
                        {i === idx && (
                            <motion.span
                                key={`${phase}-${hoverSlow}`}
                                className="hx-step-fill"
                                initial={{scaleX: reducedMotion ? 1 : 0}}
                                animate={{scaleX: 1}}
                                transition={{duration: reducedMotion ? 0 : ms / 1000, ease: "linear"}}
                            />
                        )}
                    </span>
                </li>
            ))}
        </ol>
    );
}

function Toasts({active, still}) {
    const [i, setI] = useState(0);
    useEffect(() => {
        if (!active || still) return undefined;
        setI(0);
        const id = setInterval(() => setI((n) => n + 1), 1100);
        return () => clearInterval(id);
    }, [active, still]);
    return (
        <div className="hx-toasts">
            <AnimatePresence mode="popLayout">
                {active && (
                    <motion.div
                        key={i}
                        className="hx-toast"
                        initial={{opacity: 0, y: 16, scale: 0.96}}
                        animate={{opacity: 1, y: 0, scale: 1}}
                        exit={{opacity: 0, y: -10, scale: 0.98}}
                        transition={{duration: 0.45, ease: EASE}}
                    >
                        <span className="hx-toast-dot"/>
                        {EVENTS[i % EVENTS.length]}
                        <span className="hx-toast-time">now</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function HeroVisualization({phase, reducedMotion, hoverSlow, onHoverChange}) {
    const current = reducedMotion ? "production" : phase;
    const idx = Math.max(0, HERO_PHASES.indexOf(current));
    const at = (n) => idx >= n;
    const connected = idx >= 3;
    const live = idx === 4;
    const nodePos = useIsNarrow() ? NODE_POS_NARROW : NODE_POS;

    // Pointer-driven depth: the stage tilts and the orbit layer drifts more than the app.
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, {stiffness: 90, damping: 18});
    const sy = useSpring(my, {stiffness: 90, damping: 18});
    const rotateY = useTransform(sx, [-0.5, 0.5], [-5, 5]);
    const rotateX = useTransform(sy, [-0.5, 0.5], [4, -4]);
    const orbitX = useTransform(sx, [-0.5, 0.5], [-12, 12]);
    const orbitY = useTransform(sy, [-0.5, 0.5], [-8, 8]);
    const appX = useTransform(sx, [-0.5, 0.5], [-4, 4]);
    const appY = useTransform(sy, [-0.5, 0.5], [-3, 3]);

    const onMove = (e) => {
        if (reducedMotion) return;
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
        onHoverChange(false);
    };

    return (
        <div
            className="hx-viz hx-enter-viz"
            data-phase={current}
            onMouseEnter={() => onHoverChange(true)}
            onMouseLeave={onLeave}
            onPointerMove={onMove}
            aria-label="Animation of an idea becoming a live, connected software product"
        >
            <div className="hx-product-grid-bg" aria-hidden="true"/>
            <motion.div className="hx-ambient-glow" aria-hidden="true"
                        animate={{opacity: live ? 1 : at(2) ? 0.6 : 0.25}}
                        transition={{duration: 0.8}}/>

            <Stepper idx={idx} phase={current} hoverSlow={hoverSlow} reducedMotion={reducedMotion}/>

            <motion.div className="hx-stage" style={{rotateX, rotateY, transformPerspective: 1100}} aria-hidden="true">

                {/* ---------- connections (lines, nodes, travelling data) ---------- */}
                <motion.div className="hx-orbit" style={{x: orbitX, y: orbitY}}>
                    <svg className="hx-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
                        {nodePos.map((p, i) => (
                            <motion.line
                                key={i}
                                x1="50" y1="50" x2={p.x} y2={p.y}
                                vectorEffect="non-scaling-stroke"
                                initial={false}
                                animate={{pathLength: connected ? 1 : 0, opacity: connected ? (live ? 0.55 : 1) : 0}}
                                transition={{duration: 0.7, ease: EASE, delay: connected ? i * 0.08 : 0}}
                            />
                        ))}
                    </svg>

                    {heroConnectionNodes.map((node, i) => (
                        <motion.span
                            key={node}
                            className="hx-node"
                            style={{left: `${nodePos[i].x}%`, top: `${nodePos[i].y}%`}}
                            initial={false}
                            animate={{opacity: connected ? 1 : 0, scale: connected ? 1 : 0.6}}
                            transition={{duration: 0.5, ease: EASE, delay: connected ? 0.25 + i * 0.08 : 0}}
                        >
                            {node}
                        </motion.span>
                    ))}

                    {connected && !reducedMotion && nodePos.map((p, i) => (
                        <motion.span
                            key={`dot-${i}`}
                            className="hx-packet"
                            initial={{left: "50%", top: "50%", opacity: 0}}
                            animate={{
                                left: i % 2 ? [`${p.x}%`, "50%"] : ["50%", `${p.x}%`],
                                top: i % 2 ? [`${p.y}%`, "50%"] : ["50%", `${p.y}%`],
                                opacity: [0, 1, 1, 0]
                            }}
                            transition={{duration: 1.4, ease: "easeInOut", repeat: Infinity, delay: 0.6 + i * 0.22, repeatDelay: 0.2}}
                        />
                    ))}
                </motion.div>

                {/* ---------- idea fragments ---------- */}
                <div className="hx-frags">
                    {FRAGMENTS.map((f, i) => <Fragment key={f.id} f={f} i={i} gathered={at(1)}/>)}
                </div>

                {/* ---------- idea card ---------- */}
                <AnimatePresence>
                    {idx === 0 && (
                        <motion.div
                            className="hx-idea"
                            initial={{opacity: 0, scale: 0.9, y: 8}}
                            animate={{opacity: 1, scale: 1, y: 0}}
                            exit={{opacity: 0, scale: 0.6, filter: "blur(4px)"}}
                            transition={{duration: 0.5, ease: EASE}}
                        >
                            <span className="hx-idea-spark">✦</span>
                            <TypedIdea run={!reducedMotion}/>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ---------- the product ---------- */}
                <motion.div
                    className={"hx-app" + (live ? " is-live" : "")}
                    style={{x: appX, y: appY}}
                    initial={false}
                    animate={at(1)
                        ? {opacity: 1, scale: live ? 1.03 : 1, filter: "blur(0px)"}
                        : {opacity: 0, scale: 0.86, filter: "blur(6px)"}}
                    transition={{duration: 0.8, ease: EASE}}
                >
                    <div className="hx-app-chrome">
                        <span className="hx-app-dot"/><span className="hx-app-dot"/><span className="hx-app-dot"/>
                        <span className="hx-app-url">app.yourproduct.com</span>
                        <AnimatePresence>
                            {live && (
                                <motion.span className="hx-app-live"
                                             initial={{opacity: 0, scale: 0.7}}
                                             animate={{opacity: 1, scale: 1}}
                                             exit={{opacity: 0, scale: 0.7}}
                                             transition={{duration: 0.4, ease: EASE}}>
                                    <i/> LIVE
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </div>

                    <div className="hx-app-body">
                        <aside className="hx-app-side">
                            {[0, 1, 2, 3].map((i) => (
                                <motion.span key={i} className={i === 0 ? "is-on" : ""}
                                             initial={false}
                                             animate={{opacity: at(1) ? 1 : 0, x: at(1) ? 0 : -6}}
                                             transition={{delay: at(1) ? 0.3 + i * 0.07 : 0, duration: 0.4}}/>
                            ))}
                        </aside>

                        <div className="hx-app-main">
                            <div className="hx-metrics">
                                {METRICS.map((m, i) => (
                                    <motion.div key={m.label} className="hx-metric"
                                                initial={false}
                                                animate={{opacity: at(1) ? 1 : 0, y: at(1) ? 0 : 8}}
                                                transition={{delay: at(1) ? 0.35 + i * 0.1 : 0, duration: 0.5, ease: EASE}}>
                                        <span className="hx-metric-label">{m.label}</span>
                                        <span className="hx-metric-value">
                                            <AnimatedNumber value={m.value} format={m.format} run={at(1)}/>
                                        </span>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="hx-chart">
                                <svg viewBox="0 0 200 56" preserveAspectRatio="none">
                                    <defs>
                                        <linearGradient id="hxChartFill" x1="0" x2="0" y1="0" y2="1">
                                            <stop offset="0%" stopColor="#0a0a0a" stopOpacity="0.28"/>
                                            <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0"/>
                                        </linearGradient>
                                        <linearGradient id="hxChartLine" x1="0" x2="1" y1="0" y2="0">
                                            <stop offset="0%" stopColor="#0a0a0a"/>
                                            <stop offset="100%" stopColor="#525252"/>
                                        </linearGradient>
                                    </defs>
                                    <motion.path
                                        d="M0 48 C 22 44, 34 30, 56 33 S 92 20, 114 24 S 152 8, 174 11 S 194 5, 200 4 L 200 56 L 0 56 Z"
                                        fill="url(#hxChartFill)"
                                        initial={false}
                                        animate={{opacity: at(2) ? 1 : 0}}
                                        transition={{duration: 0.8, delay: at(2) ? 0.6 : 0}}
                                    />
                                    <motion.path
                                        d="M0 48 C 22 44, 34 30, 56 33 S 92 20, 114 24 S 152 8, 174 11 S 194 5, 200 4"
                                        fill="none" stroke="url(#hxChartLine)" strokeWidth="2.2" strokeLinecap="round"
                                        vectorEffect="non-scaling-stroke"
                                        initial={false}
                                        animate={{pathLength: at(2) ? 1 : 0}}
                                        transition={{duration: at(2) ? 1.2 : 0.3, ease: EASE}}
                                    />
                                </svg>
                                {!at(2) && <span className="hx-chart-skeleton"/>}
                            </div>

                            <div className="hx-tasks">
                                {TASKS.map((t, i) => (
                                    <motion.div key={t} className="hx-task"
                                                initial={false}
                                                animate={{opacity: at(1) ? 1 : 0}}
                                                transition={{delay: at(1) ? 0.5 + i * 0.08 : 0}}>
                                        <motion.span
                                            className="hx-task-check"
                                            initial={false}
                                            animate={at(2)
                                                ? {backgroundColor: "#0a0a0a", borderColor: "#0a0a0a", scale: [1, 1.25, 1]}
                                                : {backgroundColor: "#ffffff", borderColor: "#d2d8e0", scale: 1}}
                                            transition={{delay: at(2) ? 0.35 + i * 0.35 : 0, duration: 0.4}}
                                        >
                                            <motion.svg viewBox="0 0 12 12" initial={false}
                                                        animate={{opacity: at(2) ? 1 : 0}}
                                                        transition={{delay: at(2) ? 0.45 + i * 0.35 : 0}}>
                                                <path d="M2.5 6.2 L5 8.5 L9.5 3.5" fill="none" stroke="#fff"
                                                      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                            </motion.svg>
                                        </motion.span>
                                        <span className="hx-task-label">{t}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* AI scan beam on entering Intelligence */}
                    {idx === 2 && !reducedMotion && (
                        <motion.span className="hx-scan"
                                     initial={{left: "-30%"}}
                                     animate={{left: "110%"}}
                                     transition={{duration: 1.3, ease: "easeInOut", delay: 0.1}}/>
                    )}

                    <AnimatePresence>
                        {at(2) && (
                            <motion.span className="hx-ai-chip"
                                         initial={{opacity: 0, y: -6, scale: 0.8}}
                                         animate={{opacity: 1, y: 0, scale: 1}}
                                         exit={{opacity: 0, scale: 0.8}}
                                         transition={{duration: 0.45, ease: EASE}}>
                                ✦ AI
                            </motion.span>
                        )}
                    </AnimatePresence>
                </motion.div>

                <Toasts active={live} still={reducedMotion}/>
            </motion.div>

            <motion.p className="hx-built-by" initial={false}
                      animate={{opacity: live ? 1 : 0, y: live ? 0 : 6}}
                      transition={{duration: 0.5}}>
                Built by WorkAxis
            </motion.p>

            <p className="hx-viz-sr">
                Product story: {HERO_PHASES.join(', ')}. Current: {current}.
            </p>
        </div>
    );
}
